import moment from 'moment'

import { type FilterManagerFilter } from '@webapp/components/FilterManager/types'
import {
  type AccountCode,
  type Department,
  type Location,
  type User,
  type Vendor,
} from '@webapp/models'
import { type Address } from '@webapp/models/Address'
import { type Requester } from '@webapp/models/Requester'
import {
  type RequesterFilterSelectValue,
  type AccountCodeSavedFilterSelectValue,
  type DepartmentWithBranchSavedFilterSelectValue,
  type SavedFilterSelectValue,
  type UserSavedFilterSelectValue,
} from '../types'

export const formatToVendorFilter = (
  key: string,
  data: SavedFilterSelectValue
): FilterManagerFilter<Vendor> => ({
  key,
  paramValue: data.id,
  value: formatVendorFromSavedFilterValue(data),
})

export const formatToMultipleVendorsFilter = (
  key: string,
  data: SavedFilterSelectValue[]
): FilterManagerFilter<Vendor[]> => {
  const vendorValues: Vendor[] = data.map((vendorData) =>
    formatVendorFromSavedFilterValue(vendorData)
  )

  return {
    key,
    paramValue: data.map((v) => v.id.toString()),
    value: vendorValues,
  }
}

const formatVendorFromSavedFilterValue = (
  data: SavedFilterSelectValue
): Vendor => {
  return {
    id: data.id as number,
    name: data.name,
    active: true,
    addressLineOne: null,
    addressLineTwo: null,
    city: null,
    comments: null,
    contact: null,
    country: null,
    creditcards: null,
    dateModified: null,
    email: [],
    external_id: null,
    fax: null,
    is_1099_eligible: null,
    payment_methods: null,
    payment_term_ref: null,
    phoneOne: null,
    phoneTwo: null,
    postalCode: null,
    shipping_method_ref: null,
    shipping_term_ref: null,
    state_province: null,
    type: null,
    url: null,
  }
}

export const formatToAddressFilter = (
  key: string,
  data: SavedFilterSelectValue
): FilterManagerFilter<Address> => ({
  key,
  paramValue: data.id,
  value: {
    id: data.id as number,
    name: data.name,
    active: true,
    addressAssociation: null,
    addressLineOne: null,
    addressLineTwo: null,
    branch: null,
    branch_name: null,
    city: null,
    country: null,
    created_at: null,
    is_active: null,
    is_home: null,
    last_changed_by: null,
    postalCode: null,
    state_province: null,
    updated_at: null,
  },
})

export const formatToUserFilter = (
  key: string,
  data: UserSavedFilterSelectValue
): FilterManagerFilter<User> => ({
  key,
  paramValue: data.id,
  value: {
    id: data.id as number,
    firstName: data._metadata?.firstName,
    lastName: data._metadata?.lastName,
    approval_delegatee: null,
    email: null,
    position: null,
    profile_image: null,
    department: null,
    domains: null,
    headquarter_logo: null,
    systemaccess_cache: null,
    is_approval_delegatee: null,
    created_at: null,
    updated_at: null,
    phone: null,
    address: null,
    notifications: null,
    is_active: null,
    pending_invite: null,
    role: null,
    mobile: null,
    slack_user_id: null,
    is_sso_enabled: null,
    last_changed_by: null,
    user: null,
    system_roles: null,
  },
})

export const formatToDate = (key: string, data: string) => ({
  key,
  paramValue: moment(data).format('YYYY-MM-DD'),
  value: moment(data),
})

export const formatToLocationFilter = (
  key: string,
  data: SavedFilterSelectValue
): FilterManagerFilter<Location> => ({
  key,
  paramValue: data.id,
  value: {
    id: Number(data.id),
    name: data.name,
    external_id: null,
    url: null,
    logo: null,
    language: null,
    locationTimezone: null,
    phoneOne: null,
    fax: null,
    email: null,
    active: null,
    multipleLocations: null,
    userLimit: null,
    budgetOvertureToggle: null,
    purchase_filtering: null,
    catalog_permission: null,
    separate_expense_approval: null,
    standalone_travel_approval: null,
    phoneTwo: null,
    fiscalYearEndDay: null,
    fiscalYearEndMonth: null,
    timezone: null,
    beginDate: null,
    ported: null,
    fields_cache: null,
    activated: null,
    setup_stage: null,
    localCurrency: null,
    buyerAddress: null,
    shippingAddress: null,
  },
})

export const formatToDepartmentFilter = (
  key: string,
  data: SavedFilterSelectValue
): FilterManagerFilter<Department> => ({
  key,
  paramValue: data.id,
  value: {
    active: true,
    branch: null,
    external_id: null,
    id: Number(data.id),
    name: data.name,
  },
})

export const formatToDepartmentWithBranchFilter = (
  key: string,
  data: DepartmentWithBranchSavedFilterSelectValue
): FilterManagerFilter<Department> => ({
  key,
  paramValue: data.id,
  value: {
    active: true,
    branch: data.branch,
    external_id: null,
    id: Number(data.id),
    name: data.name,
  },
})

export const formatToAccountCodeFilter = (
  key: string,
  data: AccountCodeSavedFilterSelectValue
): FilterManagerFilter<AccountCode> => ({
  key,
  paramValue: data.id,
  value: {
    active: true,
    id: Number(data.id),
    code: data.code,
    description: data.description,
    parent: null,
    account_type: null,
    code_length: null,
    applied_accounts_count: null,
  },
})

export const formatToRequesterFilter = (
  key: string,
  data: RequesterFilterSelectValue
): FilterManagerFilter<Requester> => ({
  key,
  paramValue: data.id,
  value: {
    id: data.id,
    firstName: data.firstName,
    lastName: data.lastName,
    user: null,
    email: null,
    department: null,
    location: null,
    profile_image: null,
    is_active: true,
    phone: null,
    position: null,
    role: null,
    approval_delegatee: null,
    pending_approvals_count: null,
  },
})
